import { AnalyticsFilterProvider } from "@hooks/context/useAnalyticsFilterContext";
import { ProfileProvider } from "@hooks/context/useProfileContext";
import { SidebarProvider } from "@hooks/context/useSidebarContext";
import { SupportLauncherProvider } from "@hooks/context/useSupportLauncherContext";
import { GlobalAssistant } from "@layout/GlobalAssistant/GlobalAssistant";
import { Outlet } from "react-router-dom";
import * as Sentry from "@sentry/react";

// Parent route element wrapping every route in the app. Lives once above the
// per-route layouts so that ProfileProvider and GlobalAssistant survive layout
// swaps (e.g. navigating from an org-level page to a project page). This is
// what lets the in-flight agent stream keep running after the agent itself
// triggers navigation — without it, the layout swap would unmount the chat
// mid-stream and the post-navigation explainer text would never render.
//
// BasicLayout (auth pages) now also has ProfileProvider above it; that's safe
// because ProfileProvider's fetch effects are guarded on `userProfile`, which
// is undefined when not authenticated.
//
// AnalyticsFilterProvider sits here for the same reason: the Org Insights
// period / project selections must survive navigating between sidebar sections,
// each of which is a separate route element with its own layout instance.
//
// SidebarProvider too, so the nav keeps its open/folded state across layout
// swaps.
export const RootShell = (): JSX.Element => (
  <ProfileProvider>
    <SupportLauncherProvider>
      <AnalyticsFilterProvider>
        <SidebarProvider>
          <Outlet />
          {/* The assistant is a sibling of the route content, so an error in it
            escaped every route errorElement and hit React Router's raw default
            screen ("Unexpected Application Error!") instead of ErrorPage —
            #2719. Bounded here with an empty fallback: a broken assistant now
            costs you the chat drawer, not the page you were working on. Empty
            rather than ErrorPage because this slot is a floating drawer, not a
            page region. Sentry's boundary rather than @app/components
            ErrorBoundary because that one hardcodes ErrorPage as its fallback,
            and this still reports the exception. */}
          <Sentry.ErrorBoundary fallback={<></>}>
            <GlobalAssistant />
          </Sentry.ErrorBoundary>
        </SidebarProvider>
      </AnalyticsFilterProvider>
    </SupportLauncherProvider>
  </ProfileProvider>
);
